<app-admin-shell>
  <div class="page-width-form">
    <div class="page-header">
      <div>
        <h1 class="page-header-title">{{ isEdit ? 'Edit merchant' : 'Add merchant' }}</h1>
        <p class="page-header-subtitle">
          Three groups: the business, its accounting connection, and how
          payments are taken.
        </p>
      </div>
    </div>

    <form [formGroup]="merchantForm" (ngSubmit)="onSubmit()">
    <!-- Business -->
    <div class="card section-card">
      <div class="card-body">
        <div class="section-card-header">
          <h2 class="section-card-title">Business</h2>
          <p class="section-card-subtitle">
            What your customers see when they land on the payment page.
          </p>
        </div>
        <div class="field-group">
          <div>
            <label for="name" class="form-label">Merchant name</label>
            <input
              type="text"
              class="form-control"
              [class.is-invalid]="merchantForm.get('name')?.invalid && merchantForm.get('name')?.touched"
              id="name"
              formControlName="name"
              placeholder="Enter merchant name"
              [attr.aria-invalid]="merchantForm.get('name')?.invalid && merchantForm.get('name')?.touched ? true : null"
              aria-describedby="nameHelp"
            />
            <span
              *ngIf="merchantForm.get('name')?.invalid && merchantForm.get('name')?.touched"
              id="nameHelp"
              class="form-error"
            >
              <svg class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12" y2="16.01" />
              </svg>
              Merchant name is required.
            </span>
            <span *ngIf="!(merchantForm.get('name')?.invalid && merchantForm.get('name')?.touched)" id="nameHelp" class="form-help">
              Shown as the biggest element on the customer's payment page.
            </span>
          </div>
          <div>
            <label for="paymentLink" class="form-label">Public payment URL</label>
            <div class="input-prefix-group">
              <span class="input-prefix">{{ merchantUrlHost }}</span>
              <input
                type="text"
                class="form-control"
                [class.is-invalid]="merchantForm.get('paymentLink')?.invalid && merchantForm.get('paymentLink')?.touched"
                id="paymentLink"
                formControlName="paymentLink"
                placeholder="Enter payment link"
                [attr.aria-invalid]="merchantForm.get('paymentLink')?.invalid && merchantForm.get('paymentLink')?.touched ? true : null"
                aria-describedby="paymentLinkHelp"
              />
            </div>
            <span
              *ngIf="merchantForm.get('paymentLink')?.invalid && merchantForm.get('paymentLink')?.touched"
              id="paymentLinkHelp"
              class="form-error"
            >
              <svg class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12" y2="16.01" />
              </svg>
              Public payment URL is required.
            </span>
            <span *ngIf="!(merchantForm.get('paymentLink')?.invalid && merchantForm.get('paymentLink')?.touched)" id="paymentLinkHelp" class="form-help">
              Lowercase letters, numbers and hyphens. Changing this breaks links already sent out.
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- QuickBooks -->
    <div class="card section-card">
      <div class="card-body">
        <div class="section-card-header">
          <h2 class="section-card-title">QuickBooks</h2>
          <p class="section-card-subtitle">
            Where invoices are read from and payments are written back.
          </p>
        </div>
        <div>
          <label for="quickBookCompanyId" class="form-label">
            QuickBooks company ID
          </label>
          <input
            type="text"
            class="form-control"
            id="quickBookCompanyId"
            formControlName="quickBookCompanyId"
            placeholder="Enter QuickBooks company ID"
          />
          <span class="form-help">Filled in automatically after the merchant approves access via the QuickBooks connection screen.</span>
        </div>
      </div>
    </div>

    <!-- Payments / Strictly -->
    <div class="card section-card">
      <div class="card-body">
        <div class="section-card-header">
          <h2 class="section-card-title">Payment provider</h2>
          <p class="section-card-subtitle">
            Strictly credentials used to create the payment link.
            <span class="badge badge-neutral">Encrypted at rest</span>
          </p>
        </div>
        <div class="field-group">
          <div>
            <label for="strictlyPublic" class="form-label">Strictly user</label>
            <input
              type="text"
              class="form-control"
              id="strictlyPublic"
              formControlName="strictlyPublic"
              placeholder="Enter Strictly public user"
            />
          </div>
          <div>
            <label for="strictlySecret" class="form-label">Strictly password</label>
            <div class="secret-field">
              <input
                [type]="revealStrictlySecret ? 'text' : 'password'"
                class="form-control"
                id="strictlySecret"
                formControlName="strictlySecret"
                placeholder="Enter Strictly password"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="secret-reveal"
                (click)="revealStrictlySecret = !revealStrictlySecret"
                [attr.aria-pressed]="revealStrictlySecret"
                aria-label="Reveal Strictly password"
              >
                <svg *ngIf="!revealStrictlySecret" class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg *ngIf="revealStrictlySecret" class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
            <span class="form-help">{{ isEdit ? 'Stored value hidden. Type a new one to replace it.' : 'Never shown again after saving.' }}</span>
          </div>
          <div>
            <label for="strictlyKey" class="form-label">Strictly key</label>
            <div class="secret-field">
              <input
                [type]="revealStrictlyKey ? 'text' : 'password'"
                class="form-control"
                id="strictlyKey"
                formControlName="strictlyKey"
                placeholder="Enter tokenization key"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="secret-reveal"
                (click)="revealStrictlyKey = !revealStrictlyKey"
                [attr.aria-pressed]="revealStrictlyKey"
                aria-label="Reveal Strictly key"
              >
                <svg *ngIf="!revealStrictlyKey" class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg *ngIf="revealStrictlyKey" class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
            <span class="form-help">Only the last 4 characters are ever displayed.</span>
          </div>
          <div>
            <label for="strictlyLogoUrl" class="form-label">
              Logo URL <span class="form-label-optional">optional</span>
            </label>
            <input
              type="text"
              class="form-control"
              id="strictlyLogoUrl"
              formControlName="strictlyLogoUrl"
              placeholder="https://…"
            />
            <span class="form-help">Square, at least 96px. Falls back to the initials tile if empty.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <button type="button" class="btn btn-secondary btn-lg" (click)="cancel()">
        Cancel
      </button>
      <button type="submit" class="btn btn-primary btn-lg" [disabled]="isSaving">
        <span *ngIf="!isSaving">{{ isEdit ? 'Save changes' : 'Create merchant' }}</span>
        <span *ngIf="isSaving" class="btn-loading">
          <span class="btn-spinner" aria-hidden="true"></span>
          Saving…
        </span>
      </button>
    </div>
    </form>
  </div>
</app-admin-shell>
